import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    DEFAULT_FONT_FAMILY,
    DRAG_RECT_TOOLS,
    FREEHAND_TOOLS,
    OBJ,
    TEXT_LAYER_TOOLS,
    TOOLS,
    textFontCss,
} from '../constants';
import {
    isAspectLocked,
    makeInk,
    makeLink,
    makeNote,
    makeShape,
    makeStamp,
    makeText,
    pickObject,
    rectFromDrag,
} from '../engine/objects';
import {
    FIT_TOLERANCE,
    TEXT_PAD,
    cellTextObject,
    measuredTextHeight,
    paintObjects,
} from '../engine/drawObjects';
import { buildReplacementBox } from '../engine/editText';
import { ensureFontsFor, textStylesOf } from '../engine/fonts';
import {
    readTextBlocks,
    readTextLines,
    renderPageCanvas,
} from '../engine/pdfEngine';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { docClipboardFor, docFlowList, docSelectionTableIds, orderDocSelection, usePdfEditorStore } from '../state/usePdfEditorStore';
import { offsetAt, rangeRects } from '../engine/textLayout';
import TextEditor from './TextEditor';
import StaticText from './StaticText';
import { docHitTest, registerDocHitTester } from './docHitTest';
import { pageAtPoint, registerPageDropTarget } from './pageDropTargets';
import { TABLE_MIN_COL, addRow, colLeft, colWidths, pointToCell, stepCell, verticalCell } from '../engine/tableLayout';
import SelectionFrame from './SelectionFrame';

const MAX_DPR = 2;
const MIN_DRAG = 3;

/** A sticky note's text scales with the note, and is always the UI face. */
/**
 * Document mode: the paragraph a click on blank page space belongs to, the way
 * Word decides — the one whose lines the click is level with, else the nearest
 * one above or below. Header/footer/page-number boxes (`role`) are not body
 * text and are left to their own double-click.
 */
function nearestParagraph(objects, point) {
    let best = null;
    let bestScore = Infinity;
    for (const o of objects) {
        if (o.role || (o.type !== OBJ.TEXT && o.type !== OBJ.NOTE)) continue;
        const dy = point.y < o.y ? o.y - point.y : point.y > o.y + o.h ? point.y - (o.y + o.h) : 0;
        const dx = point.x < o.x ? o.x - point.x : point.x > o.x + o.w ? point.x - (o.x + o.w) : 0;
        // Vertical distance decides; horizontal only breaks ties between
        // columns at the same height.
        const score = dy * 1000 + dx;
        if (score < bestScore) { bestScore = score; best = o; }
    }
    return best;
}

/**
 * Document mode drag-select: stretch the open editor's selection from where the
 * press landed to where the pointer is now. The editor mounts on the press, so
 * the anchor is resolved here rather than at pointerdown; a pointer that has
 * left the box above or below runs the selection to its start or end, the way
 * dragging past a paragraph does in Word.
 */
function extendEditorSelection(root, anchor, point) {
    const el = root?.querySelector('[contenteditable="true"]');
    const sel = window.getSelection();
    if (!el || !sel || !document.caretRangeFromPoint) return;
    const a = document.caretRangeFromPoint(anchor.x, anchor.y);
    if (!a || !el.contains(a.startContainer)) return;
    let focusNode;
    let focusOffset;
    const b = document.caretRangeFromPoint(point.x, point.y);
    if (b && el.contains(b.startContainer)) {
        focusNode = b.startContainer;
        focusOffset = b.startOffset;
    } else {
        const box = el.getBoundingClientRect();
        const edge = document.createRange();
        edge.selectNodeContents(el);
        edge.collapse(point.y < box.top);
        focusNode = edge.startContainer;
        focusOffset = edge.startOffset;
    }
    sel.setBaseAndExtent(a.startContainer, a.startOffset, focusNode, focusOffset);
}

/** The text block under a page-space point (2pt of slack), the smallest if they nest. */
function blockAtPoint(targets, point) {
    let best = null;
    for (const t of targets || []) {
        if (point.x < t.x - 2 || point.x > t.x + t.width + 2) continue;
        if (point.y < t.y - 2 || point.y > t.y + t.height + 2) continue;
        if (!best || t.width * t.height < best.width * best.height) best = t;
    }
    return best;
}

/**
 * Where the browser puts the first baseline of a text box: px from the top of
 * its content box, for this font, size and line height.
 *
 * The canvas painter puts it at 0.82em, which is what anchors an edited
 * paragraph on the source baseline. A contenteditable puts it at half-leading
 * plus the font's own ascent — different for every face — and Chrome rounds
 * both to whole pixels, so no formula reproduces it. Measure it instead, on a
 * detached copy, so the editor can be lifted onto the painter's baseline and
 * the text does not move when a box opens for editing.
 */
function cssFirstBaseline(style, fontPx, lineHeightPx) {
    if (typeof document === 'undefined') return null;
    const box = document.createElement('div');
    box.style.cssText = 'position:absolute;left:-9999px;top:0;visibility:hidden;padding:0;margin:0;border:0;white-space:pre';
    box.style.fontFamily = textFontCss(style);
    box.style.fontSize = `${fontPx}px`;
    box.style.fontWeight = style.bold ? '700' : '400';
    box.style.fontStyle = style.italic ? 'italic' : 'normal';
    box.style.lineHeight = `${lineHeightPx}px`;
    const probe = document.createElement('span');
    probe.style.cssText = 'display:inline-block;width:0;height:0;vertical-align:baseline';
    box.append(probe, document.createTextNode('Hxg'));
    document.body.appendChild(box);
    const offset = probe.getBoundingClientRect().top - box.getBoundingClientRect().top;
    box.remove();
    return Number.isFinite(offset) ? offset : null;
}

// `cssFirstBaseline` per font + size, so a page of paragraphs measures each look
// once. Emptied when a web font finishes loading: a measurement taken against the
// fallback face would put every baseline of that family in the wrong place.
const baselineCache = new Map();
if (typeof document !== 'undefined' && document.fonts?.addEventListener) {
    document.fonts.addEventListener('loadingdone', () => baselineCache.clear());
}
function firstBaseline(style, fontPx, lineHeightPx) {
    const key = [style.fontStack, style.fontFamily, style.bold, style.italic, fontPx, lineHeightPx].join('|');
    if (!baselineCache.has(key)) baselineCache.set(key, cssFirstBaseline(style, fontPx, lineHeightPx));
    return baselineCache.get(key);
}

/**
 * The box a paragraph is set in as browser text — the live editor and, in
 * document mode, every read-only paragraph (StaticText) — so the two are
 * pixel-identical and opening one for typing moves nothing.
 *
 * Its first baseline sits exactly where the canvas painter puts it (box top +
 * TEXT_PAD + 0.82em), measured per font; the half-leading lift is the fallback.
 */
function textBoxStyle(obj, scale, { fontSize, lineHeight, documentMode, clickToEditText, fontStyle = obj }) {
    const baseline = firstBaseline(fontStyle, fontSize * scale, lineHeight * fontSize * scale);
    return {
        // Type on the same background the export will paint. Click-to-edit and
        // document mode type on the page itself: a white wash would fade the text.
        background: obj.background && obj.background !== 'transparent'
            ? obj.background
            : (documentMode || clickToEditText ? 'transparent' : 'rgba(255,255,255,0.88)'),
        left: obj.x * scale,
        top: baseline != null
            ? (obj.y + fontSize * 0.82) * scale - baseline
            : obj.y * scale - ((lineHeight - 1) / 2) * fontSize * scale,
        boxSizing: 'content-box',
        // Outside document mode, justified text spreads to the box's exact width:
        // it has to meet the painter's justified lines on the canvas. In document
        // mode the browser text IS what is shown, so it wraps at the same width as
        // the layout (`wrapTextLines`) — the line breaks, the box height and the
        // selection highlight then all agree.
        width: Math.max(4, (obj.w - TEXT_PAD * 2) * scale
            * (obj.align === 'justify' && !documentMode ? 1 : FIT_TOLERANCE)),
        minHeight: Math.max(12, obj.h - TEXT_PAD * 2) * scale,
        padding: TEXT_PAD * scale,
        // Document mode edits the text in place — no box around it.
        outline: documentMode ? 'none' : '1px solid #e68a00',
        outlineOffset: 0,
        lineHeight: `${lineHeight * fontSize * scale}px`,
        textAlign: obj.align || 'left',
        transform: obj.rotation ? `rotate(${obj.rotation}deg)` : undefined,
        transformOrigin: 'center',
    };
}

const noteFontSize = (obj) => Math.max(7, Math.min(11, obj.h / 6));
const noteStyle = (obj) => ({
    fontFamily: DEFAULT_FONT_FAMILY,
    fontStack: null,
    fontSize: noteFontSize(obj),
    color: '#1C1815',
    bold: false,
    italic: false,
    underline: false,
});

/** Cursor per tool, so the page always says what a click will do. */
const CURSORS = {
    [TOOLS.SELECT]: 'default',
    [TOOLS.HAND]: 'grab',
    [TOOLS.TEXT]: 'text',
    [TOOLS.EDIT_TEXT]: 'text',
    [TOOLS.PENCIL]: 'crosshair',
    [TOOLS.HIGHLIGHT]: 'crosshair',
};

/**
 * One rendered PDF page: the pdf.js raster, the annotation overlay painted with
 * the same routine the exporter uses, and all pointer interaction.
 *
 * Annotations are stored in the page's *view space* (points, y down). The
 * page's own `/Rotate` is already baked into that space by pdf.js; extra
 * rotation the user applies is a CSS transform here and an added `/Rotate` on
 * export, so annotations always turn with the content beneath them.
 */
export default function PageView({
    page,
    index,
    pdf,
    // The same document with the replaced paragraphs deleted, when one could be
    // built. Only the page bitmap comes from it: the ink probe and the text
    // layer still read the original, because they exist to measure and target
    // the very text this copy no longer has.
    basePdf,
    strippedIds,
    scale,
    objects,
    searchHits,
    activeHitId,
    onRequestFocus,
    // Draft view: the page furniture and the pictures come off, which is what
    // makes a two-hundred-page file scroll.
    plain = false,
    labels,
    // The source page's own pictures, in its view space — the "replace image"
    // tool's targets.
    imagePlacements,
    onReplaceImage,
}) {
    const { t } = useTranslation();
    const wrapRef = useRef(null);
    const baseRef = useRef(null);
    const overlayRef = useRef(null);
    const gestureRef = useRef(null);

    const activeTool = usePdfEditorStore((s) => s.activeTool);
    const toolSettings = usePdfEditorStore((s) => s.toolSettings);
    const selectedId = usePdfEditorStore((s) => s.selectedId);
    const documentMode = usePdfEditorStore((s) => s.documentMode);
    const clickToEditText = usePdfEditorStore((s) => s.clickToEditText);
    const showGrid = usePdfEditorStore((s) => s.showGrid);
    const showMargins = usePdfEditorStore((s) => s.showMargins);
    const margins = usePdfEditorStore((s) => s.margins);
    const editingId = usePdfEditorStore((s) => s.editingId);
    // What another page's drag would drop here, drawn faded where it would land.
    const dragGhost = usePdfEditorStore((s) => (s.dragGhost?.uid === page.uid ? s.dragGhost.obj : null));
    const addObject = usePdfEditorStore((s) => s.addObject);
    const addPendingEdit = usePdfEditorStore((s) => s.addPendingEdit);
    const addObjects = usePdfEditorStore((s) => s.addObjects);
    const updateObject = usePdfEditorStore((s) => s.updateObject);
    const removeObject = usePdfEditorStore((s) => s.removeObject);
    const duplicateObject = usePdfEditorStore((s) => s.duplicateObject);
    const selectObject = usePdfEditorStore((s) => s.selectObject);
    const setEditing = usePdfEditorStore((s) => s.setEditing);
    const setTool = usePdfEditorStore((s) => s.setTool);
    const beginGesture = usePdfEditorStore((s) => s.beginGesture);
    const setSignModal = usePdfEditorStore((s) => s.setSignModal);
    const setTextSelection = usePdfEditorStore((s) => s.setTextSelection);
    const setLinkDraft = usePdfEditorStore((s) => s.setLinkDraft);

    const [visible, setVisible] = useState(index < 3);
    const [rendered, setRendered] = useState(false);
    const [draft, setDraft] = useState(null);
    const [textLines, setTextLines] = useState(null);
    const [textBlocks, setTextBlocks] = useState(null);
    const [repaintTick, setRepaintTick] = useState(0);

    const pageW = page.width;
    const pageH = page.height;

    /**
     * Where the page's *own* content sits now, if the paper was changed.
     *
     * pdf.js reports text, blocks and search hits in the source page's
     * coordinates, and nothing about a new sheet moves them — so every one of
     * those has to be carried through this before it can be drawn over the
     * page, and pointer coordinates carried back through it before they can be
     * compared with one.
     */
    const fit = useMemo(() => (page.contentScale && page.contentScale !== 1
        ? { k: page.contentScale, dx: page.contentOffset?.x || 0, dy: page.contentOffset?.y || 0 }
        : null), [page.contentScale, page.contentOffset]);

    /** Source-space rect to page space. Accepts either rect spelling. */
    const toPage = useCallback((r) => {
        if (!fit || !r) return r;
        const w = r.w ?? r.width;
        const h = r.h ?? r.height;
        const mapped = {
            ...r,
            x: r.x * fit.k + fit.dx,
            y: r.y * fit.k + fit.dy,
        };
        if (r.w != null) mapped.w = w * fit.k;
        if (r.width != null) mapped.width = w * fit.k;
        if (r.h != null) mapped.h = h * fit.k;
        if (r.height != null) mapped.height = h * fit.k;
        return mapped;
    }, [fit]);

    /** Page-space point back to source space, for hit-testing pdf geometry. */
    const toSource = useCallback((p) => (fit
        ? { x: (p.x - fit.dx) / fit.k, y: (p.y - fit.dy) / fit.k }
        : p), [fit]);
    const rotated = page.userRotation % 180 === 90;
    const displayW = (rotated ? pageH : pageW) * scale;
    const displayH = (rotated ? pageW : pageH) * scale;

    const selected = useMemo(
        () => objects.find((o) => o.id === selectedId) || null,
        [objects, selectedId],
    );
    const editingCell = usePdfEditorStore((s) => s.editingCell);
    const editing = useMemo(
        () => objects.find((o) => o.id === editingId) || null,
        [objects, editingId],
    );

    /* ------------------------------------------------------- lazy visibility */

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return undefined;
        const io = new IntersectionObserver(
            ([entry]) => setVisible(entry.isIntersecting),
            { root: null, rootMargin: '600px 0px' },
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    /* ------------------------------------------------------------ base page */

    useEffect(() => {
        if (!visible || !pdf || page.sourceIndex == null) return undefined;
        let cancelled = false;
        const controller = new AbortController();
        const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
        const renderScale = Math.min(4, scale * dpr);

        // Editor order, so a duplicated page edited on one copy does not show
        // the other copy's holes.
        const source = basePdf || pdf;
        const pageNumber = basePdf ? index + 1 : page.sourceIndex + 1;

        renderPageCanvas(source, pageNumber, renderScale, controller.signal)
            .then((canvas) => {
                if (cancelled || !baseRef.current) return;
                const target = baseRef.current;
                if (!fit) {
                    target.width = canvas.width;
                    target.height = canvas.height;
                    target.getContext('2d').drawImage(canvas, 0, 0);
                    setRendered(true);
                    return;
                }
                // Different paper: the page's own content is *fitted* on to the
                // new sheet, uniformly and centred — letterboxed, never
                // stretched — because that is exactly what the exporter does.
                // Stretching it here is how the preview and the download came
                // to disagree about the shape of every glyph.
                target.width = Math.max(1, Math.round(pageW * renderScale));
                target.height = Math.max(1, Math.round(pageH * renderScale));
                const ctx = target.getContext('2d');
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, target.width, target.height);
                ctx.drawImage(
                    canvas,
                    fit.dx * renderScale,
                    fit.dy * renderScale,
                    (canvas.width / renderScale) * fit.k * renderScale,
                    (canvas.height / renderScale) * fit.k * renderScale,
                );
                setRendered(true);
            })
            .catch(() => {});

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [visible, pdf, basePdf, index, page.sourceIndex, scale, fit, pageW, pageH]);

    /* --------------------------------------------------------- overlay paint */

    /**
     * What the overlay leaves out: the box being typed into (the live editor is
     * drawing that one), and any patch whose paragraph the stripped render has
     * already removed.
     *
     * A patch over text that really went away covers whatever the page drew
     * behind that paragraph — a watermark, a rule, a tint — and covers it for
     * nothing. The export drops it for the same reason; this is the canvas
     * finally agreeing with the file it produces.
     */
    const skipIds = useMemo(() => {
        const ids = new Set();
        // A table being edited cell by cell stays painted — only that one cell's text is left out (see `skipCell`).
        if (editingId && !editingCell) ids.add(editingId);
        // Document mode shows its paragraphs as browser text (StaticText), not paint.
        if (documentMode) for (const o of objects) if (o.type === OBJ.TEXT) ids.add(o.id);
        if (strippedIds?.size) {
            for (const o of objects) {
                if (o.type === OBJ.WHITEOUT && o.patchFor && strippedIds.has(o.patchFor)) {
                    ids.add(o.id);
                }
            }
        }
        return ids;
    }, [editingId, editingCell, objects, strippedIds, documentMode]);

    // A layout effect, not a passive one: the moment a box opens for typing the live editor
    // draws its text and the overlay must stop drawing it IN THE SAME FRAME. Painted a frame
    // later, the old canvas text and the new DOM text sit on top of each other and the line
    // flashes darker on every click.
    useLayoutEffect(() => {
        const canvas = overlayRef.current;
        if (!canvas) return;
        const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
        const pixelScale = scale * dpr;
        canvas.width = Math.max(1, Math.round(pageW * pixelScale));
        canvas.height = Math.max(1, Math.round(pageH * pixelScale));

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Draft view leaves the pictures out — decoding and scaling them is most
        // of what a long document spends its time on, and draft exists to not
        // spend it.
        const painted = plain ? objects.filter((obj) => obj.type !== OBJ.IMAGE) : objects;
        paintObjects(ctx, painted, pixelScale, {
            skipIds,
            skipCell: editingId && editingCell ? { id: editingId, r: editingCell.r, c: editingCell.c } : null,
            // Document mode shows [placeholders] as yellow chips (display only — never exported).
            markPlaceholders: documentMode,
            onImageReady: () => setRepaintTick((t) => t + 1),
        });
        if (draft) {
            paintObjects(ctx, [draft], pixelScale, {
                onImageReady: () => setRepaintTick((t) => t + 1),
            });
        }
        if (dragGhost) {
            ctx.save();
            ctx.globalAlpha = 0.55;
            paintObjects(ctx, [dragGhost], pixelScale, { onImageReady: () => setRepaintTick((t) => t + 1) });
            ctx.restore();
        }
    }, [objects, draft, dragGhost, plain, scale, pageW, pageH, skipIds, editingId, editingCell, repaintTick, documentMode]);

    // A box can be set in a family that is still downloading (a font just picked
    // in the options bar, or one detected in the PDF). The canvas paints
    // synchronously, so repaint once the faces are actually usable.
    useEffect(() => {
        let cancelled = false;
        const bump = () => { if (!cancelled) setRepaintTick((t) => t + 1); };
        ensureFontsFor(textStylesOf(objects)).then(bump);
        // The wait above is capped, so a slow connection can give up before a face
        // lands. Whenever the browser does finish loading one, paint again — the
        // canvas does not re-lay-out by itself the way page text does.
        document.fonts?.addEventListener?.('loadingdone', bump);
        return () => {
            cancelled = true;
            document.fonts?.removeEventListener?.('loadingdone', bump);
        };
    }, [objects]);

    /* -------------------------------------------------------- pdf text layer */

    // Click-to-edit (the PDF edit tool): the Select tool is also the Edit Text
    // tool. Its paragraphs get no buttons of their own — those would sit on
    // top of anything placed over the text — so the pointer handlers look the
    // block up instead, and only once nothing placed on the page was hit.
    const autoBlocks = clickToEditText && activeTool === TOOLS.SELECT;
    const needsTextLayer = TEXT_LAYER_TOOLS.has(activeTool) || autoBlocks;
    // Edit text works on paragraphs; underline / strike-out work on single
    // lines, so each tool gets the granularity it needs.
    const wantsBlocks = activeTool === TOOLS.EDIT_TEXT || autoBlocks;
    // The paragraphs on offer (set once `visibleTargets` is known, further
    // down) and the one under the pointer, for the hover outline.
    const blockTargetsRef = useRef([]);
    const [hoverBlockKey, setHoverBlockKey] = useState(null);

    useEffect(() => {
        if (!needsTextLayer || !pdf || page.sourceIndex == null) return undefined;
        const cached = wantsBlocks ? textBlocks : textLines;
        if (cached) return undefined;
        // A block's font — the document's OWN embedded face — is only known once
        // pdf.js has rendered the page (see `readFontDescriptors`). Read before
        // that and every paragraph comes back as a Helvetica lookalike, so
        // opening one for editing visibly restyles it. Click-to-edit reads the
        // blocks as soon as a page mounts, so it has to wait for the render.
        if (wantsBlocks && !rendered) return undefined;

        let cancelled = false;
        const pageNumber = page.sourceIndex + 1;
        const read = wantsBlocks
            ? async () => {
                // Once a paragraph has been edited the page is displayed from
                // the stripped preview document, so a page first shown after
                // that never rendered from `pdf` and its fonts never loaded
                // there. A throwaway low-resolution render loads them.
                if (basePdf) await renderPageCanvas(pdf, pageNumber, 0.25).catch(() => null);
                return readTextBlocks(pdf, pageNumber);
            }
            : () => readTextLines(pdf, pageNumber);
        read()
            .then((rows) => {
                if (cancelled) return;
                if (wantsBlocks) setTextBlocks(rows);
                else setTextLines(rows);
            })
            .catch(() => {
                if (cancelled) return;
                if (wantsBlocks) setTextBlocks([]);
                else setTextLines([]);
            });
        return () => { cancelled = true; };
    }, [needsTextLayer, wantsBlocks, textBlocks, textLines, pdf, basePdf, page.sourceIndex, rendered]);

    /* -------------------------------------------------------------- geometry */

    const pointFromEvent = useCallback((e) => {
        const canvas = overlayRef.current;
        const rect = canvas.getBoundingClientRect();
        // The layer may be CSS-rotated; undo that rotation around its centre so
        // pointer coordinates land back in unrotated page space.
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const angle = (-(page.userRotation || 0) * Math.PI) / 180;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const lx = dx * Math.cos(angle) - dy * Math.sin(angle);
        const ly = dx * Math.sin(angle) + dy * Math.cos(angle);
        return {
            x: (lx + (pageW * scale) / 2) / scale,
            y: (ly + (pageH * scale) / 2) / scale,
        };
    }, [page.userRotation, pageW, pageH, scale]);

    /** The inverse of `pointFromEvent`: page space back to viewport pixels. */
    const clientFromPoint = useCallback((point) => {
        const rect = overlayRef.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const lx = point.x * scale - (pageW * scale) / 2;
        const ly = point.y * scale - (pageH * scale) / 2;
        const angle = ((page.userRotation || 0) * Math.PI) / 180;
        return {
            x: cx + lx * Math.cos(angle) - ly * Math.sin(angle),
            y: cy + lx * Math.sin(angle) + ly * Math.cos(angle),
        };
    }, [page.userRotation, pageW, pageH, scale]);

    /**
     * Document mode: the paragraph and character a page-space point means —
     * above a paragraph is its start, below it its end, as Word reads a click.
     */
    const paraHit = useCallback((point) => {
        const para = nearestParagraph(objects, point);
        if (!para) return null;
        if (point.y > para.y + para.h) return { id: para.id, offset: String(para.text ?? '').length };
        if (point.y < para.y) return { id: para.id, offset: 0 };
        return { id: para.id, offset: offsetAt(para, point) };
    }, [objects]);

    // So an object dragged from another page can be dropped on this one.
    useEffect(() => registerPageDropTarget(page.uid, (clientX, clientY) => {
        const el = overlayRef.current;
        if (!el) return null;
        const r = el.getBoundingClientRect();
        if (clientX < r.left || clientX > r.right || clientY < r.top || clientY > r.bottom) return null;
        return pointFromEvent({ clientX, clientY });
    }), [page.uid, pointFromEvent]);

    // So a drag that started on another page can find this one's text.
    useEffect(() => {
        if (!documentMode) return undefined;
        return registerDocHitTester(page.uid, (clientX, clientY) => {
            const el = overlayRef.current;
            if (!el) return null;
            const r = el.getBoundingClientRect();
            if (clientX < r.left || clientX > r.right || clientY < r.top || clientY > r.bottom) return null;
            return paraHit(pointFromEvent({ clientX, clientY }));
        });
    }, [documentMode, page.uid, paraHit, pointFromEvent]);

    const clampRect = useCallback((rect) => ({
        ...rect,
        x: Math.max(0, Math.min(pageW - 4, rect.x)),
        y: Math.max(0, Math.min(pageH - 4, rect.y)),
        w: Math.max(2, Math.min(pageW - rect.x, rect.w)),
        h: Math.max(2, Math.min(pageH - rect.y, rect.h)),
    }), [pageW, pageH]);

    /* -------------------------------------------------------- object creation */

    const commitDragRect = useCallback((rect) => {
        const settings = toolSettings;
        switch (activeTool) {
            case TOOLS.SHAPE:
                return makeShape(settings.shape.kind, clampRect(rect), {
                    ...settings.shape,
                    flipX: rect.flipX,
                    flipY: rect.flipY,
                });
            case TOOLS.CHECK:
                return makeStamp(OBJ.CHECK, clampRect(rect), settings.check);
            case TOOLS.CROSS:
                return makeStamp(OBJ.CROSS, clampRect(rect), settings.cross);
            case TOOLS.LINK:
                return makeLink(clampRect(rect), settings.link);
            default:
                return null;
        }
    }, [activeTool, toolSettings, clampRect]);

    const defaultRectForTool = useCallback((point) => {
        const size = {
            [TOOLS.CHECK]: { w: 18, h: 18 },
            [TOOLS.CROSS]: { w: 18, h: 18 },
            [TOOLS.SHAPE]: { w: 120, h: 80 },
            [TOOLS.LINK]: { w: 120, h: 16 },
        }[activeTool] || { w: 100, h: 40 };
        return { x: point.x, y: point.y, w: size.w, h: size.h };
    }, [activeTool]);

    /* ---------------------------------------------------------- text editing */

    const startTextAt = useCallback((point) => {
        const settings = toolSettings.text;
        const width = Math.min(260, Math.max(80, pageW - point.x - 24));
        const object = makeText(
            { x: point.x, y: point.y, w: width, h: settings.fontSize * settings.lineHeight + TEXT_PAD * 2 },
            settings,
            '',
        );
        addObject(page.uid, object);
        // Disarm first, like the shape tools do: otherwise the next click drops
        // another empty box instead of selecting the one just typed. Order
        // matters — `setTool` clears `editingId`, so open the box afterwards.
        setTool(TOOLS.SELECT);
        setEditing(object.id);
    }, [toolSettings.text, pageW, addObject, page.uid, setEditing, setTool]);

    /**
     * Document mode, on a page with no text: its first paragraph, at the top
     * margin and across the text column, set like the document's body text
     * (the last plain paragraph anywhere in it) rather than the text tool's
     * defaults — typing on a fresh page should look like the pages before it.
     */
    const startDocParagraph = useCallback(() => {
        const state = usePdfEditorStore.getState();
        const body = Object.values(state.objectsByUid)
            .flat()
            .filter((o) => o && o.type === OBJ.TEXT && !o.role && o.fontSize <= 14)
            .pop();
        const style = body
            ? {
                fontFamily: body.fontFamily,
                fontStack: body.fontStack,
                fontSize: body.fontSize,
                lineHeight: body.lineHeight,
                color: body.color,
                align: body.align === 'justify' ? 'justify' : 'left',
            }
            : toolSettings.text;
        const left = body ? body.x : 72;
        const top = state.docMargins?.top ?? (body ? Math.min(body.y, 72) : 72);
        const object = makeText(
            {
                x: left,
                y: top,
                w: Math.max(80, pageW - left * 2),
                h: style.fontSize * (style.lineHeight || 1.35) + TEXT_PAD * 2,
            },
            { ...toolSettings.text, ...style },
            '',
        );
        addObject(page.uid, object);
        setEditing(object.id);
    }, [toolSettings.text, pageW, addObject, page.uid, setEditing]);

    /**
     * "Edit text" on an existing paragraph.
     *
     * The measuring and calibration live in the engine, where the site's
     * cut-down version of this tool uses them too. What stays here is what is
     * actually PageView's: the page being looked at, and where the result goes.
     */
    // Which blocks are being turned into editable boxes right now.
    //
    // Building one can take a moment — the document's own face may still be
    // fetching, and the cover colour is sampled from a fresh render — and every
    // click in the meantime used to start another. The paragraph then ended up
    // under two or three stacked copies of itself.
    const buildingRef = useRef(new Set());
    // …and which one is being built right now, so the page can say so. A click
    // that takes a second with no sign of life reads as a broken button.
    const [buildingKey, setBuildingKey] = useState(null);

    const startEditPdfBlock = useCallback(async (block) => {
        const key = block.id || `${block.x},${block.y}`;
        if (buildingRef.current.has(key)) return;
        buildingRef.current.add(key);
        setBuildingKey(key);
        try {
        const built = await buildReplacementBox(block, {
            pdf,
            pageNumber: page.sourceIndex != null ? page.sourceIndex + 1 : null,
            // The source page's own size: the block, the colour probe and the
            // measurement all speak that, whatever sheet the page is on now.
            pageW: fit ? pageW / fit.k : pageW,
            pageH: fit ? pageH / fit.k : pageH,
            // On resized paper the base canvas is a letterboxed copy, so a
            // source-space rect would sample the wrong pixels; the builder
            // re-renders the region instead.
            baseCanvas: !fit && rendered ? baseRef.current : null,
            defaults: toolSettings.text,
        });
        if (!built) return;
        // The builder works in the source page's space. `replaces` stays there
        // — the exporter cuts the old text out before it rescales anything —
        // while the box and its patch are carried on to the sheet.
        const box = fit ? { ...toPage(built.box), fontSize: built.box.fontSize * fit.k } : built.box;
        const patch = fit ? toPage(built.patch) : built.patch;
        if (fit) box.replaces = built.box.replaces;
        if (clickToEditText) {
            // Nothing changes until something is typed (see `addPendingEdit`):
            // the page keeps its own rendering, so the click moves nothing.
            const { replaces, ...rest } = box;
            addPendingEdit(page.uid, rest, { replaces, patch });
        } else {
            // One step, so undo takes the patch back with the box it belongs to.
            addObjects(page.uid, [patch, box], { select: box.id });
        }
        // The tool stays armed: editing one paragraph should not send the user
        // back to the ribbon before editing the next.
        setEditing(box.id);
        } finally {
            buildingRef.current.delete(key);
            setBuildingKey((current) => (current === key ? null : current));
        }
    }, [pageW, pageH, rendered, pdf, page.sourceIndex, toolSettings.text, addObjects, addPendingEdit, clickToEditText, page.uid, setEditing, fit, toPage]);

    const decoratePdfLine = useCallback((line) => {
        const settings = activeTool === TOOLS.UNDERLINE
            ? toolSettings.underline
            : toolSettings.strikeout;
        const y = activeTool === TOOLS.UNDERLINE
            ? line.y + line.height * 0.94
            // Through the middle of the lowercase letters: the box puts the baseline
            // at 0.82em from its top, and ~0.3em above that is where a strike belongs.
            // (0.58 of the box landed 0.15em above the baseline and read as an underline.)
            : line.y + line.height * 0.45;
        addObject(page.uid, makeShape(
            OBJ.LINE,
            { x: line.x, y, w: line.width, h: 0.1 },
            { stroke: settings.color, strokeWidth: settings.strokeWidth, fill: 'transparent' },
        ), { select: false });
    }, [activeTool, toolSettings, addObject, page.uid]);

    /* --------------------------------------------------------------- pointer */

    const handlePointerDown = useCallback((e) => {
        if (e.button !== 0) return;
        // Suppress the compatibility mouse events for the creating tools: their
        // default action moves focus to the page body, which would immediately
        // blur (and therefore discard) a text box created by this very click.
        // The select tool must NOT cancel them, or `dblclick` never fires and
        // text can no longer be opened for editing.
        if (activeTool !== TOOLS.SELECT && activeTool !== TOOLS.HAND) e.preventDefault();
        onRequestFocus?.(index);
        const point = pointFromEvent(e);

        if (activeTool === TOOLS.HAND) return;

        if (activeTool === TOOLS.TEXT) {
            startTextAt(point);
            return;
        }

        if (activeTool === TOOLS.NOTE) {
            const object = makeNote(
                clampRect({ x: point.x, y: point.y, w: 150, h: 96 }),
                toolSettings.note,
                '',
            );
            addObject(page.uid, object);
            setTool(TOOLS.SELECT);
            setEditing(object.id);
            return;
        }

        if (activeTool === TOOLS.SIGN) {
            setSignModal(true);
            return;
        }

        if (FREEHAND_TOOLS.has(activeTool)) {
            e.currentTarget.setPointerCapture(e.pointerId);
            gestureRef.current = { mode: 'free', points: [[point.x, point.y]] };
            const settings = activeTool === TOOLS.PENCIL ? toolSettings.pencil : toolSettings.highlight;
            setDraft(makeInk(
                [[point.x, point.y]],
                settings,
                activeTool === TOOLS.HIGHLIGHT ? 'highlight' : 'pen',
            ));
            return;
        }

        if (DRAG_RECT_TOOLS.has(activeTool)) {
            e.currentTarget.setPointerCapture(e.pointerId);
            gestureRef.current = { mode: 'rect', start: point };
            return;
        }

        // "Edit text" on a paragraph that has already been replaced. Its block
        // is no longer offered by the overlay — a second block click would
        // stack a duplicate patch — so the click arrives here instead. Re-open
        // the box that stands in for the paragraph, otherwise editing one is a
        // one-shot action and the text can never be corrected again.
        if (activeTool === TOOLS.EDIT_TEXT) {
            const box = pickObject(objects, point);
            if (box && (box.type === OBJ.TEXT || box.type === OBJ.NOTE)) {
                selectObject(box.id);
                setEditing(box.id);
            } else {
                selectObject(null);
            }
            return;
        }

        if (documentMode && activeTool === TOOLS.SELECT) {
            const store = usePdfEditorStore.getState();
            // Shift+click extends the selection to here, across paragraphs and
            // pages — the way to select more than a drag can reach on screen.
            if (e.shiftKey) {
                const anchor = store.docSelection?.anchor
                    || (store.editingId ? { id: store.editingId, offset: store.textSelection.start } : null);
                const focus = paraHit(point);
                if (anchor && focus) {
                    e.preventDefault();
                    store.setDocSelection({ anchor, focus });
                    e.currentTarget.setPointerCapture(e.pointerId);
                    gestureRef.current = { mode: 'textselect', anchor: { x: e.clientX, y: e.clientY }, anchorHit: anchor, multi: true, moved: true };
                    return;
                }
            }
            if (store.docSelection) store.setDocSelection(null);
        }

        // Select tool — move or deselect.
        const hit = pickObject(objects, point);
        if (!hit && documentMode) {
            // A document is typed into like Word: a click anywhere on blank page
            // space puts the caret in the nearest paragraph — no Add-text tool.
            e.preventDefault();
            const para = nearestParagraph(objects, point);
            if (para) {
                const below = point.y > para.y + para.h;
                // Level with (or above) its lines: the caret goes to the nearest
                // character on the nearest line. Below the paragraph: its end.
                const at = below ? null : clientFromPoint({
                    x: Math.min(para.x + para.w - TEXT_PAD - 0.5, Math.max(para.x + TEXT_PAD + 0.5, point.x)),
                    y: Math.min(para.y + para.h - TEXT_PAD - 1, Math.max(para.y + TEXT_PAD + 1, point.y)),
                });
                if (para.id === editingId) {
                    // Already typing in it: just move the caret, as Word does.
                    const el = wrapRef.current?.querySelector('[contenteditable="true"]');
                    const range = at && document.caretRangeFromPoint?.(at.x, at.y);
                    const sel = window.getSelection();
                    if (el && sel) {
                        if (range && el.contains(range.startContainer)) {
                            sel.removeAllRanges();
                            sel.addRange(range);
                        } else {
                            sel.selectAllChildren(el);
                            sel.collapseToEnd();
                        }
                    }
                    return;
                }
                selectObject(para.id);
                setEditing(para.id, null, at);
                if (at) {
                    e.currentTarget.setPointerCapture(e.pointerId);
                    gestureRef.current = { mode: 'textselect', anchor: at, anchorHit: paraHit(point), moved: false };
                }
                return;
            }
            // A page with no text yet: start its first paragraph at the top
            // margin, in the document's own body style.
            startDocParagraph();
            return;
        }
        if (!hit) {
            selectObject(null);
            // Click-to-edit: a paragraph of the page's own text opens for editing.
            const block = autoBlocks ? blockAtPoint(blockTargetsRef.current, point) : null;
            if (block) {
                e.preventDefault();
                startEditPdfBlock(block.source || block);
            }
            return;
        }
        // Document mode types and selects like Word: a press on a paragraph
        // opens it with the caret under the pointer, and dragging from there
        // selects a range — no first click to "activate" the box.
        if (documentMode && (hit.type === OBJ.TEXT || hit.type === OBJ.NOTE) && hit.id !== editingId) {
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            const at = { x: e.clientX, y: e.clientY };
            selectObject(hit.id);
            setEditing(hit.id, null, at);
            gestureRef.current = {
                mode: 'textselect',
                anchor: at,
                anchorHit: { id: hit.id, offset: offsetAt(hit, point) },
                moved: false,
            };
            return;
        }
        e.currentTarget.setPointerCapture(e.pointerId);
        selectObject(hit.id);
        gestureRef.current = {
            mode: 'move',
            start: point,
            origin: { x: hit.x, y: hit.y },
            id: hit.id,
            moved: false,
            // Document mode: text is typed into where it sits, never dragged around.
            noDrag: documentMode && (hit.type === OBJ.TEXT || hit.type === OBJ.NOTE),
            // Where the pointer holds it, so a drop elsewhere keeps the same grip.
            grab: { x: point.x - hit.x, y: point.y - hit.y },
            // Free to change pages — not a header/footer/page number (one per page),
            // not a paragraph standing in for this page's own text (`replaces`, its
            // cover patch), which only means anything on the page it came from.
            crossPage: !hit.role && !hit.replaces && !hit.pending && !hit.patchFor,
        };
    }, [
        activeTool, objects, pointFromEvent, onRequestFocus, index, startTextAt, documentMode,
        toolSettings, addObject, page.uid, setEditing, setTool, clampRect, selectObject, setSignModal,
        autoBlocks, startEditPdfBlock, clientFromPoint, startDocParagraph, editingId, paraHit,
    ]);

    const handlePointerMove = useCallback((e) => {
        const gesture = gestureRef.current;
        if (!gesture) {
            // Click-to-edit: outline the paragraph a click would open — unless
            // something placed on the page sits there and would take the click.
            if (autoBlocks) {
                const point = pointFromEvent(e);
                const block = pickObject(objects, point) ? null : blockAtPoint(blockTargetsRef.current, point);
                const key = block ? (block.id || `${block.x},${block.y}`) : null;
                setHoverBlockKey((current) => (current === key ? current : key));
            }
            return;
        }
        if (gesture.mode === 'textselect') {
            // A few pixels of slack, so a plain click keeps its caret.
            if (!gesture.moved && Math.hypot(e.clientX - gesture.anchor.x, e.clientY - gesture.anchor.y) < 3) return;
            gesture.moved = true;
            // Into another paragraph (on this page or any other): the selection
            // stops being one box's and becomes the document's — painted over
            // the canvas, with the box closed.
            const over = docHitTest(e.clientX, e.clientY);
            if (gesture.multi || (over && gesture.anchorHit && over.id !== gesture.anchorHit.id)) {
                if (over && gesture.anchorHit) {
                    gesture.multi = true;
                    usePdfEditorStore.getState().setDocSelection({ anchor: gesture.anchorHit, focus: over });
                }
                return;
            }
            extendEditorSelection(wrapRef.current, gesture.anchor, { x: e.clientX, y: e.clientY });
            return;
        }
        const point = pointFromEvent(e);

        if (gesture.mode === 'free') {
            gesture.points.push([point.x, point.y]);
            const settings = activeTool === TOOLS.PENCIL ? toolSettings.pencil : toolSettings.highlight;
            setDraft(makeInk(
                gesture.points,
                settings,
                activeTool === TOOLS.HIGHLIGHT ? 'highlight' : 'pen',
            ));
            return;
        }

        if (gesture.mode === 'rect') {
            const rect = rectFromDrag(gesture.start, point, { square: e.shiftKey });
            gesture.rect = rect;
            const preview = commitDragRect(rect);
            if (preview) setDraft(preview);
            return;
        }

        if (gesture.mode === 'move') {
            if (gesture.noDrag) return;
            const dx = point.x - gesture.start.x;
            const dy = point.y - gesture.start.y;
            if (!gesture.moved) {
                if (Math.abs(dx) * scale < MIN_DRAG && Math.abs(dy) * scale < MIN_DRAG) return;
                gesture.moved = true;
                beginGesture();
            }
            updateObject(page.uid, gesture.id, {
                x: gesture.origin.x + dx,
                y: gesture.origin.y + dy,
            }, { transient: true });
            // Over another page: show there, faded, where the drop would put it.
            const over = gesture.crossPage ? pageAtPoint(e.clientX, e.clientY) : null;
            const store = usePdfEditorStore.getState();
            if (over && over.uid !== page.uid) {
                const obj = objects.find((o) => o.id === gesture.id);
                if (obj) {
                    store.setDragGhost({
                        uid: over.uid,
                        obj: { ...obj, x: over.point.x - gesture.grab.x, y: over.point.y - gesture.grab.y },
                    });
                }
            } else if (store.dragGhost) {
                store.setDragGhost(null);
            }
            return;
        }

        if (gesture.mode === 'resize') {
            const { origin, handle } = gesture;
            let { x, y, w, h } = origin;
            const dx = point.x - gesture.start.x;
            // A text box only has corners, and they set the width: its height
            // always follows the text (below), so vertical travel is ignored.
            const dy = gesture.type === OBJ.TEXT ? 0 : point.y - gesture.start.y;
            if (handle.includes('e')) w = origin.w + dx;
            if (handle.includes('s')) h = origin.h + dy;
            if (handle.includes('w')) { x = origin.x + dx; w = origin.w - dx; }
            if (handle.includes('n')) { y = origin.y + dy; h = origin.h - dy; }
            if (w < 4) { x = Math.min(x, origin.x + origin.w - 4); w = 4; }
            if (h < 4) { y = Math.min(y, origin.y + origin.h - 4); h = 4; }

            // Locked ratio: re-derive the other side and re-anchor to whichever
            // edge the user is NOT dragging, so the picture grows out of the
            // handle instead of drifting across the page.
            if (gesture.lockAspect && gesture.ratio > 0) {
                const corner = /^(nw|ne|se|sw)$/.test(handle);
                const drivenByWidth = corner
                    ? Math.abs(dx) >= Math.abs(dy)
                    : handle === 'e' || handle === 'w';
                if (drivenByWidth) h = Math.max(4, w / gesture.ratio);
                else w = Math.max(4, h * gesture.ratio);

                const right = origin.x + origin.w;
                const bottom = origin.y + origin.h;
                x = handle.includes('w') ? right - w : origin.x;
                y = handle.includes('n') ? bottom - h : origin.y;
                if (handle === 'e' || handle === 'w') y = origin.y;
                if (handle === 'n' || handle === 's') x = origin.x;
            }

            const patch = { x, y, w, h };

            if (gesture.type === OBJ.TEXT) {
                // Resizing a paragraph re-wraps it and re-measures the height —
                // and gives a box whose height was once pinned back to its text.
                const target = objects.find((o) => o.id === gesture.id);
                if (target) {
                    patch.autoHeight = true;
                    patch.h = Math.max(
                        target.fontSize * target.lineHeight + TEXT_PAD * 2,
                        measuredTextHeight({ ...target, ...patch }),
                    );
                }
            }

            updateObject(page.uid, gesture.id, patch, { transient: true });
            return;
        }

        if (gesture.mode === 'rotate') {
            const angle = (Math.atan2(point.y - gesture.center.y, point.x - gesture.center.x) * 180) / Math.PI;
            let next = gesture.originRotation + (angle - gesture.startAngle);
            if (e.shiftKey) next = Math.round(next / 15) * 15;
            updateObject(page.uid, gesture.id, {
                rotation: Math.round(((next % 360) + 360) % 360),
            }, { transient: true });
        }
    }, [
        pointFromEvent, activeTool, toolSettings, commitDragRect, scale,
        beginGesture, updateObject, page.uid, objects,
    ]);

    const handlePointerUp = useCallback((e) => {
        const gesture = gestureRef.current;
        gestureRef.current = null;
        if (!gesture) return;
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }

        // The editor opened on the press and owns the selection now.
        if (gesture.mode === 'textselect') return;

        // A drag let go over another page moves the object there; in document mode the
        // pages then re-flow around it, on this page as much as any other.
        if (gesture.mode === 'move' && gesture.moved) {
            const store = usePdfEditorStore.getState();
            const over = gesture.crossPage ? pageAtPoint(e.clientX, e.clientY) : null;
            if (over && over.uid !== page.uid) {
                store.moveObjectToPage(page.uid, over.uid, gesture.id, {
                    x: over.point.x - gesture.grab.x,
                    y: over.point.y - gesture.grab.y,
                }, gesture.origin, over.point.y);
                return;
            }
            if (store.dragGhost) store.setDragGhost(null);
            store.settleAfterDrag(page.uid, gesture.id, gesture.origin, pointFromEvent(e).y);
            return;
        }

        if (gesture.mode === 'free') {
            setDraft(null);
            if (gesture.points.length < 2) return;
            const settings = activeTool === TOOLS.PENCIL ? toolSettings.pencil : toolSettings.highlight;
            addObject(
                page.uid,
                makeInk(gesture.points, settings, activeTool === TOOLS.HIGHLIGHT ? 'highlight' : 'pen'),
                { select: false },
            );
            return;
        }

        if (gesture.mode === 'rect') {
            setDraft(null);
            const point = pointFromEvent(e);
            let rect = gesture.rect || rectFromDrag(gesture.start, point);
            const tiny = rect.w * scale < MIN_DRAG || rect.h * scale < MIN_DRAG;
            if (tiny) rect = { ...defaultRectForTool(gesture.start), flipX: false, flipY: false };
            const object = commitDragRect(rect);
            if (!object) return;
            addObject(page.uid, object);
            if (activeTool !== TOOLS.CHECK && activeTool !== TOOLS.CROSS) setTool(TOOLS.SELECT);
            // A link with no target exports nothing, so ask for one right away.
            if (object.type === OBJ.LINK) setLinkDraft({ uid: page.uid, id: object.id, isNew: true });
            return;
        }

        // Document mode: a plain click (no drag) on a text box opens it for editing
        // right away — a document editor shouldn't need a second click to type.
        // Click-to-edit does the same; a drag still moves the box there.
        if ((documentMode || clickToEditText) && gesture.mode === 'move' && !gesture.moved) {
            const hit = objects.find((o) => o.id === gesture.id);
            if (hit && (hit.type === OBJ.TEXT || hit.type === OBJ.NOTE)) {
                e.preventDefault();
                setEditing(hit.id, null, { x: e.clientX, y: e.clientY });
            } else if (hit && hit.type === OBJ.TABLE) {
                e.preventDefault();
                const p = pointFromEvent(e);
                const cell = pointToCell(hit, p.x, p.y);
                if (cell) setEditing(hit.id, cell);
            }
            return;
        }

        // A plain click only selects — text opens for editing on double click,
        // so that dragging and resizing a box never turns into typing in it.
    }, [
        activeTool, toolSettings, addObject, page.uid, pointFromEvent, scale,
        commitDragRect, defaultRectForTool, setTool, setLinkDraft,
        documentMode, clickToEditText, objects, setEditing,
    ]);

    const handleDoubleClick = useCallback((e) => {
        if (activeTool !== TOOLS.SELECT) return;
        const hit = pickObject(objects, pointFromEvent(e));
        if (!hit) return;
        if (hit.type === OBJ.TABLE) {
            e.preventDefault();
            const p = pointFromEvent(e);
            const cell = pointToCell(hit, p.x, p.y);
            if (cell) setEditing(hit.id, cell);
            return;
        }
        if (hit.type === OBJ.TEXT || hit.type === OBJ.NOTE) {
            e.preventDefault();
            setEditing(hit.id);
        } else if (hit.type === OBJ.LINK) {
            e.preventDefault();
            setLinkDraft({ uid: page.uid, id: hit.id, isNew: false });
        }
    }, [activeTool, objects, pointFromEvent, setEditing, setLinkDraft, page.uid]);

    const handleHandleDown = useCallback((e, handle) => {
        e.stopPropagation();
        e.preventDefault();
        if (!selected) return;
        overlayRef.current?.setPointerCapture?.(e.pointerId);
        beginGesture();
        gestureRef.current = {
            mode: 'resize',
            handle,
            id: selected.id,
            type: selected.type,
            start: pointFromEvent(e),
            origin: { x: selected.x, y: selected.y, w: selected.w, h: selected.h },
            // Hold Shift to break a locked ratio for one drag, as everywhere else.
            lockAspect: selected.type === OBJ.IMAGE && isAspectLocked(selected) && !e.shiftKey,
            ratio: selected.h > 0 ? selected.w / selected.h : 0,
        };
    }, [selected, beginGesture, pointFromEvent]);

    const handleRotateDown = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!selected) return;
        overlayRef.current?.setPointerCapture?.(e.pointerId);
        beginGesture();
        const center = { x: selected.x + selected.w / 2, y: selected.y + selected.h / 2 };
        const point = pointFromEvent(e);
        gestureRef.current = {
            mode: 'rotate',
            id: selected.id,
            center,
            startAngle: (Math.atan2(point.y - center.y, point.x - center.x) * 180) / Math.PI,
            originRotation: selected.rotation || 0,
        };
    }, [selected, beginGesture, pointFromEvent]);

    /* ----------------------------------------------------------- text editor */

    // The thing the inline editor edits: a normal box, or ONE table cell presented
    // as a throwaway text object positioned over that cell.
    const editTarget = useMemo(() => {
        if (!editing) return null;
        if (editing.type === OBJ.TABLE && editingCell) {
            // The same pseudo box the table paints that cell with (merged area, per-cell style, vertical middle).
            return {
                ...cellTextObject(editing, editingCell.r, editingCell.c),
                id: `${editing.id}:${editingCell.r}:${editingCell.c}`,
                autoHeight: true,
            };
        }
        return editing;
    }, [editing, editingCell]);

    // The document selection's highlight on this page (document mode).
    const docSelection = usePdfEditorStore((s) => s.docSelection);
    const docSelRects = useMemo(() => {
        if (!documentMode || !docSelection) return [];
        const list = docFlowList(usePdfEditorStore.getState());
        const ends = orderDocSelection(docSelection, list);
        if (!ends) return [];
        const rects = [];
        for (let i = ends.from.i; i <= ends.to.i; i += 1) {
            const { uid, obj } = list[i];
            if (uid !== page.uid) continue;
            const len = String(obj.text ?? '').length;
            const start = i === ends.from.i ? ends.from.off : 0;
            // A paragraph the selection runs on past gets the line-break sliver.
            const end = i === ends.to.i ? ends.to.off : len + 1;
            rangeRects(obj, start, end).forEach((r, n) => rects.push({ ...r, key: `${obj.id}:${n}` }));
        }
        // A table the selection runs past is copied whole, so it shows whole.
        const tables = new Set(docSelectionTableIds(usePdfEditorStore.getState(), docSelection));
        for (const obj of objects) {
            if (tables.has(obj.id)) rects.push({ x: obj.x, y: obj.y, w: obj.w, h: obj.h, key: obj.id });
        }
        return rects;
        // `objects` stands in for the store's layout: the rects move when it does.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentMode, docSelection, objects, page.uid]);

    // The editor's font, size and line pitch (a note's are its own) — see textBoxStyle.
    const editStyle = editTarget
        ? (editTarget.type === OBJ.NOTE ? noteStyle(editTarget) : editTarget)
        : null;
    const editFontSize = editStyle?.fontSize || 12;
    const editLineHeight = editTarget?.lineHeight || 1.3;
    // Typing is applied live (transient, no history entry per keystroke), so
    // each burst of it opens one undo step of its own — a new one when another
    // box is typed into or after a pause, as a word processor groups typing.
    const typingRef = useRef({ id: null, at: 0 });
    // A pending paragraph (PDF click-to-edit) records its own step when its
    // first change activates it; a second snapshot there would undo back to an
    // invisible box sitting on the page.
    const noteTyping = useCallback((id, { pending = false } = {}) => {
        const now = Date.now();
        const last = typingRef.current;
        if (!pending && (last.id !== id || now - last.at > 1000)) beginGesture();
        typingRef.current = { id, at: now };
    }, [beginGesture]);

    const handleTextInput = useCallback(({ text, marks }) => {
        if (!editing) return;
        noteTyping(editing.id, { pending: !!editing.pending });
        const next = { ...editing, text, marks };
        const patch = { text, marks };
        if (editing.type === OBJ.TEXT && editing.autoHeight !== false) {
            patch.h = Math.max(editing.fontSize * editing.lineHeight + TEXT_PAD * 2, measuredTextHeight(next));
        }
        updateObject(page.uid, editing.id, patch, { transient: true });
    }, [editing, updateObject, page.uid, noteTyping]);

    // Route edits either to a table cell (rewrite that one cell's text) or a text box.
    const handleEditInput = useCallback(({ text, marks }) => {
        if (editing?.type === OBJ.TABLE && editingCell) {
            const { r, c } = editingCell;
            const cells = editing.cells.map((row, ri) => (ri === r ? row.map((v, ci) => (ci === c ? text : v)) : row));
            noteTyping(`${editing.id}:${r}:${c}`);
            updateObject(page.uid, editing.id, { cells }, { transient: true });
            return;
        }
        handleTextInput({ text, marks });
    }, [editing, editingCell, updateObject, page.uid, handleTextInput, noteTyping]);

    const finishEditing = useCallback(() => {
        // An emptied box is only junk when it has no background: with one, the
        // user has deliberately wiped a line of the PDF and the patch must stay.
        // A replaced paragraph emptied on purpose is the same: removing the box
        // would take its patch with it and bring the old words back.
        const blank = editing
            && editing.type === OBJ.TEXT
            && !editing.replaces
            && !String(editing.text || '').trim()
            && (!editing.background || editing.background === 'transparent');
        if (blank) removeObject(page.uid, editing.id);
        setEditing(null);
    }, [editing, removeObject, page.uid, setEditing]);

    // Document mode: pasting paragraphs copied from the document rebuilds them
    // as paragraphs, formatting and spacing included (see `pasteDocParagraphs`).
    // Anything else pastes as plain text inside the box.
    const handleDocPaste = useCallback((text) => {
        const rich = docClipboardFor(text);
        if (!rich || (rich.paragraphs.length < 2 && !rich.paragraphs[0].table) || !editing || editing.type !== OBJ.TEXT) return false;
        const store = usePdfEditorStore.getState();
        const { start, end } = store.textSelection;
        const caret = store.pasteDocParagraphs({ id: editing.id, start, end }, rich.paragraphs);
        if (caret) store.setEditing(caret.id, null, { offset: caret.offset });
        return true;
    }, [editing]);

    /**
     * Moving around a table from the keyboard, as Word and Google Docs do:
     * Tab / Shift+Tab to the next / previous cell (Tab in the last cell adds a
     * row), and the arrow keys into the neighbouring cell once the caret is at
     * the edge of this one. Returns true when it moved the editing.
     */
    const handleCellKey = useCallback((e, edges) => {
        if (!editing || editing.type !== OBJ.TABLE || !editingCell) return false;
        const t = editing;
        const { r, c } = editingCell;
        const lengthOf = (cell) => String(t.cells?.[cell.r]?.[cell.c] ?? '').length;
        const go = (cell, at) => {
            setEditing(t.id, cell, { offset: at === 'end' ? lengthOf(cell) : 0 });
            return true;
        };
        if (e.key === 'Tab') {
            const next = stepCell(t, r, c, e.shiftKey ? -1 : 1);
            if (next) return go(next, 'end');
            if (e.shiftKey) return true; // first cell: stay
            // Past the last cell: a new row, and on into its first cell.
            const grown = addRow(t);
            updateObject(page.uid, t.id, {
                rows: grown.rows, cells: grown.cells, rowHeights: grown.rowHeights,
                merges: grown.merges, cellStyles: grown.cellStyles, rowMin: grown.rowMin,
            });
            setEditing(t.id, { r: t.rows, c: 0 }, { offset: 0 });
            return true;
        }
        if (!edges.collapsed || e.shiftKey || e.altKey || e.metaKey || e.ctrlKey) return false;
        if (e.key === 'ArrowUp' && edges.firstLine) { const n = verticalCell(t, r, c, -1); return n ? go(n, 'end') : false; }
        if (e.key === 'ArrowDown' && edges.lastLine) { const n = verticalCell(t, r, c, 1); return n ? go(n, 'start') : false; }
        if (e.key === 'ArrowLeft' && edges.atStart) { const n = stepCell(t, r, c, -1); return n ? go(n, 'end') : false; }
        if (e.key === 'ArrowRight' && edges.atEnd) { const n = stepCell(t, r, c, 1); return n ? go(n, 'start') : false; }
        return false;
    }, [editing, editingCell, setEditing, updateObject, page.uid]);

    /**
     * Dragging the rule between two columns resizes those two, the table's width
     * unchanged — the way a border is dragged in Word. Row heights follow the
     * re-wrapped text (the store recomputes them on `colFracs`).
     */
    const startColumnDrag = useCallback((e, t, boundary) => {
        e.preventDefault();
        e.stopPropagation();
        const widths = colWidths(t);
        const startX = e.clientX;
        beginGesture();
        const onMove = (ev) => {
            const dx = (ev.clientX - startX) / scale;
            const pair = widths[boundary - 1] + widths[boundary];
            const left = Math.min(pair - TABLE_MIN_COL, Math.max(TABLE_MIN_COL, widths[boundary - 1] + dx));
            const next = widths.slice();
            next[boundary - 1] = left;
            next[boundary] = pair - left;
            updateObject(page.uid, t.id, { colFracs: next.map((w) => w / t.w) }, { transient: true });
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    }, [beginGesture, scale, updateObject, page.uid]);

    // A table cell just closes (never deletes the table); a box uses finishEditing.
    const handleEditFinish = useCallback(() => {
        if (editing?.type === OBJ.TABLE) { setEditing(null); return; }
        finishEditing();
    }, [editing, setEditing, finishEditing]);

    /* -------------------------------------------------------------- rendering */

    // Document mode is typed into anywhere on the page (see the pointer
    // handler), so the page shows Word's I-beam rather than an arrow.
    const cursor = (autoBlocks && hoverBlockKey ? 'text' : null)
        || (documentMode && activeTool === TOOLS.SELECT ? 'text' : null)
        || CURSORS[activeTool]
        || (DRAG_RECT_TOOLS.has(activeTool) ? 'crosshair' : 'default');

    /**
     * Regions offered to the text-layer tools.
     *
     * The layer stays up while a box is open — it renders *below* the textarea,
     * so caret clicks still land in the editor while a click on another
     * paragraph moves straight on to it. Blocks already replaced by an edited
     * box drop out, otherwise a second click would stack a duplicate on top.
     */
    // Carried into page space, with the source row kept: the layout below and
    // the patch test compare these against objects, while the edit builder
    // needs the coordinates pdf.js actually reported.
    const textTargets = useMemo(() => {
        const rows = wantsBlocks ? textBlocks : textLines;
        if (!Array.isArray(rows) || !fit) return rows;
        return rows.map((row) => ({ ...toPage(row), source: row }));
    }, [wantsBlocks, textBlocks, textLines, fit, toPage]);
    const showTextLayer = needsTextLayer && Array.isArray(textTargets);

    const visibleTargets = useMemo(() => {
        if (!showTextLayer) return [];
        if (!wantsBlocks) return textTargets;
        const patches = objects.filter(
            (o) => (o.type === OBJ.TEXT && o.background && o.background !== 'transparent')
                || (o.type === OBJ.WHITEOUT && o.patchFor),
        );
        if (!patches.length) return textTargets;

        return textTargets.filter((target) => !patches.some((patch) => {
            const overlapW = Math.min(target.x + target.width, patch.x + patch.w) - Math.max(target.x, patch.x);
            const overlapH = Math.min(target.y + target.height, patch.y + patch.h) - Math.max(target.y, patch.y);
            if (overlapW <= 0 || overlapH <= 0) return false;
            const area = Math.max(1, target.width * target.height);
            return (overlapW * overlapH) / area > 0.6;
        }));
    }, [showTextLayer, wantsBlocks, textTargets, objects]);
    blockTargetsRef.current = autoBlocks ? visibleTargets : [];

    return (
        <div
            ref={wrapRef}
            data-page-index={index}
            className="relative flex-none"
            style={{ width: displayW, height: displayH }}
        >
            <div
                className={`absolute left-1/2 top-1/2 origin-center bg-white ${
                    plain ? 'shadow-none ring-1 ring-[#ECE8E4] dark:ring-zinc-800' : 'shadow-[0_2px_14px_rgba(28,24,21,0.16)]'
                }`}
                style={{
                    width: pageW * scale,
                    height: pageH * scale,
                    transform: `translate(-50%, -50%) rotate(${page.userRotation}deg)`,
                }}
            >
                {page.sourceIndex == null ? (
                    <div className="absolute inset-0 bg-white" />
                ) : (
                    <canvas
                        ref={baseRef}
                        className="absolute inset-0 h-full w-full"
                        style={{ opacity: rendered ? 1 : 0, transition: 'opacity 120ms' }}
                    />
                )}

                {/* Word's View → Gridlines: a measuring grid, drawn over the
                    page and never exported — the export paints objects, and
                    this is chrome, not an object. */}
                {showGrid ? (
                    <div
                        className="pointer-events-none absolute inset-0"
                        style={{
                            backgroundImage:
                                'linear-gradient(to right, rgba(28,24,21,0.09) 1px, transparent 1px),'
                                + 'linear-gradient(to bottom, rgba(28,24,21,0.09) 1px, transparent 1px)',
                            backgroundSize: `${18 * scale}px ${18 * scale}px`,
                        }}
                    />
                ) : null}

                {/* document selection across paragraphs */}
                {docSelRects.map((r) => (
                    <div
                        key={r.key}
                        className="pointer-events-none absolute bg-[#3390FF]/30"
                        style={{ left: r.x * scale, top: r.y * scale, width: r.w * scale, height: r.h * scale }}
                    />
                ))}

                {/* search results */}
                {searchHits?.map((hit, i) => (
                    <div
                        key={`${hit.lineId}-${i}`}
                        className={`pointer-events-none absolute rounded-[2px] ${
                            hit.id === activeHitId ? 'bg-primary/50 ring-1 ring-primary' : 'bg-yellow-300/45'
                        }`}
                        style={{
                            left: toPage(hit).x * scale,
                            top: toPage(hit).y * scale,
                            width: Math.max(3, toPage(hit).width * scale),
                            height: Math.max(6, toPage(hit).height * scale),
                        }}
                    />
                ))}

                <canvas
                    ref={overlayRef}
                    className="absolute inset-0 h-full w-full touch-none"
                    style={{ cursor }}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                    onPointerLeave={autoBlocks ? () => setHoverBlockKey(null) : undefined}
                    onDoubleClick={handleDoubleClick}
                />

                {/* Page margins, as guides. They are chrome: they say where the
                    running text and the notes will sit, and they are never
                    exported. */}
                {showMargins ? (
                    <div
                        className="pointer-events-none absolute border border-dashed border-[#6D43C8]/45"
                        style={{
                            left: margins.left * scale,
                            top: margins.top * scale,
                            right: margins.right * scale,
                            bottom: margins.bottom * scale,
                        }}
                    />
                ) : null}

                {/* Track changes, marked on the page: what was added is boxed in
                    green, what was edited in amber, and what is proposed for
                    deletion is greyed and struck — it is still there, because
                    nothing goes until somebody accepts it. */}
                {objects.some((obj) => obj.reviewMark || obj.pendingDelete) ? (
                    <div className="pointer-events-none absolute inset-0">
                        {objects.filter((obj) => obj.reviewMark || obj.pendingDelete).map((obj) => (
                            <div
                                key={`rev-${obj.id}`}
                                className={`absolute rounded-[3px] border ${
                                    obj.pendingDelete
                                        ? 'border-[#D33A2C] bg-[#D33A2C]/12'
                                        : obj.reviewMark === 'add'
                                            ? 'border-[#1E9E5E] bg-[#1E9E5E]/10'
                                            : 'border-[#B7791F] bg-[#B7791F]/10'
                                }`}
                                style={{
                                    left: obj.x * scale,
                                    top: obj.y * scale,
                                    width: Math.max(4, obj.w * scale),
                                    height: Math.max(4, obj.h * scale),
                                }}
                            >
                                {obj.pendingDelete ? (
                                    <span
                                        className="absolute left-0 right-0 top-1/2 h-[1.5px] bg-[#D33A2C]"
                                        aria-hidden
                                    />
                                ) : null}
                            </div>
                        ))}
                    </div>
                ) : null}

                {/* The source document's own pictures, offered for replacement.
                    Same shape as the text-block layer below it: the container
                    must not take the pointer, or it would eat every click that
                    misses a picture. */}
                {activeTool === TOOLS.REPLACE_IMAGE && imagePlacements?.length ? (
                    <div className="pointer-events-none absolute inset-0">
                        {imagePlacements.map((placement) => {
                            const box = toPage(placement);
                            return (
                                <button
                                    key={placement.id}
                                    type="button"
                                    onClick={() => onReplaceImage?.(page.uid, placement)}
                                    title={t('pdfEditor.images.replaceHint')}
                                    className="pointer-events-auto absolute rounded-[3px] border-2 border-dashed border-[#A8A099] transition-colors hover:border-primary hover:bg-primary/10"
                                    style={{
                                        left: box.x * scale,
                                        top: box.y * scale,
                                        width: Math.max(8, box.w * scale),
                                        height: Math.max(8, box.h * scale),
                                    }}
                                />
                            );
                        })}
                    </div>
                ) : null}

                {/* editable regions — above the overlay so they take the click */}
                {showTextLayer && autoBlocks ? (
                    // Click-to-edit draws no regions, only the outline of the
                    // paragraph under the pointer (or the one being built);
                    // the canvas below takes the click.
                    <div className="pointer-events-none absolute inset-0">
                        {visibleTargets
                            .filter((target) => {
                                const key = target.id || `${target.x},${target.y}`;
                                return key === hoverBlockKey || key === buildingKey;
                            })
                            .map((target) => (
                                <div
                                    key={target.id || `${target.x},${target.y}`}
                                    className={`absolute rounded-[2px] border border-dashed border-primary ${
                                        buildingKey === (target.id || `${target.x},${target.y}`)
                                            ? 'animate-pulse bg-primary/15'
                                            : 'bg-primary/5'
                                    }`}
                                    style={{
                                        left: (target.x - 2) * scale,
                                        top: (target.y - 2) * scale,
                                        width: Math.max(6, (target.width + 4) * scale),
                                        height: Math.max(6, (target.height + 4) * scale),
                                    }}
                                />
                            ))}
                    </div>
                ) : showTextLayer ? (
                    // The layer itself must not take the pointer: it spans the
                    // whole page, and anything outside a region has to reach the
                    // canvas below — that is where a click on an already-edited
                    // paragraph re-opens its box.
                    <div className="pointer-events-none absolute inset-0">
                        {visibleTargets.map((target) => (
                            <button
                                key={target.id}
                                type="button"
                                onClick={() => (wantsBlocks
                                    ? startEditPdfBlock(target.source || target)
                                    : decoratePdfLine(target))}
                                className={`pointer-events-auto absolute rounded-[2px] border border-dashed transition-colors hover:bg-primary/10 ${
                                    wantsBlocks
                                        ? 'border-[#A8A099] hover:border-primary'
                                        : 'border-transparent hover:border-primary'
                                } ${
                                    buildingKey === (target.id || `${target.x},${target.y}`)
                                        ? 'animate-pulse border-primary bg-primary/15'
                                        : ''
                                }`}
                                style={{
                                    left: (target.x - (wantsBlocks ? 2 : 0)) * scale,
                                    top: (target.y - (wantsBlocks ? 2 : 0)) * scale,
                                    width: Math.max(6, (target.width + (wantsBlocks ? 4 : 0)) * scale),
                                    height: Math.max(6, (target.height + (wantsBlocks ? 4 : 0)) * scale),
                                }}
                                title={target.text}
                            />
                        ))}
                    </div>
                ) : null}

                {/* document mode: every other paragraph as read-only browser text */}
                {documentMode
                    ? objects
                        .filter((o) => o.type === OBJ.TEXT && o.id !== editTarget?.id)
                        .map((o) => (
                            <StaticText
                                key={o.id}
                                obj={o}
                                scale={scale}
                                boxStyle={textBoxStyle(o, scale, {
                                    fontSize: o.fontSize || 12, lineHeight: o.lineHeight || 1.3, documentMode, clickToEditText,
                                })}
                            />
                        ))
                    : null}

                {/* inline text editing — a normal box, or one table cell (editTarget) */}
                {editTarget ? (
                    <TextEditor
                        object={editTarget}
                        // A note is a fixed style: it scales with the sticky, so
                        // it cannot inherit the text tool's size.
                        styleObject={editTarget.type === OBJ.NOTE ? noteStyle(editTarget) : editTarget}
                        scale={scale}
                        onChange={handleEditInput}
                        onFinish={handleEditFinish}
                        onSelection={setTextSelection}
                        onPasteText={documentMode ? handleDocPaste : undefined}
                        onEdgeKey={editing?.type === OBJ.TABLE ? handleCellKey : undefined}
                        selectPlaceholders={documentMode}
                        className="absolute overflow-hidden border-0 outline-none"
                        style={{
                            ...textBoxStyle(editTarget, scale, {
                                fontSize: editFontSize, lineHeight: editLineHeight, documentMode, clickToEditText, fontStyle: editStyle,
                            }),
                            // Unchanged (pending): the page still shows the PDF's own
                            // words underneath, so the editor's copy stays invisible —
                            // only the caret and selection show — until the first change.
                            ...(editTarget.pending ? {
                                WebkitTextFillColor: 'transparent',
                                caretColor: editTarget.color || '#000000',
                            } : {}),
                        }}
                    />
                ) : null}

                {/* column rules of the selected table — drag one to resize the two
                    columns beside it */}
                {selected?.type === OBJ.TABLE && activeTool === TOOLS.SELECT && selected.cols > 1 && !selected.rotation
                    ? Array.from({ length: selected.cols - 1 }, (_, n) => n + 1).map((boundary) => (
                        <div
                            key={`col-${boundary}`}
                            role="separator"
                            aria-orientation="vertical"
                            onPointerDown={(e) => startColumnDrag(e, selected, boundary)}
                            className="group absolute z-[5] flex cursor-col-resize justify-center"
                            style={{
                                left: colLeft(selected, boundary) * scale - 4,
                                top: selected.y * scale,
                                width: 8,
                                height: selected.h * scale,
                            }}
                        >
                            <span className="h-full w-[2px] bg-primary opacity-0 transition-opacity group-hover:opacity-70" />
                        </div>
                    ))
                    : null}

                {/* selection chrome — handles stay up while typing so a
                    paragraph can be widened without leaving the editor */}
                {selected && (activeTool === TOOLS.SELECT || editing) && !(documentMode && (selected.type === OBJ.TEXT || selected.type === OBJ.NOTE)) ? (
                    <SelectionFrame
                        object={selected}
                        scale={scale}
                        labels={labels}
                        editing={!!editing}
                        onHandleDown={handleHandleDown}
                        onRotateDown={handleRotateDown}
                        onDelete={() => removeObject(page.uid, selected.id)}
                        onDuplicate={() => duplicateObject(page.uid, selected.id)}
                    />
                ) : null}
            </div>

            <div className="pointer-events-none absolute -bottom-6 left-0 right-0 text-center text-[11px] font-medium text-[#A8A099] dark:text-zinc-500">
                {labels.pageNumber(index + 1)}
            </div>
        </div>
    );
}
